'use client';

import { useRef, SetStateAction } from 'react';
import { Camera } from 'react-camera-pro';

import {
  ButtonCancelCamera,
  ButtonCaptureImage,
  CameraLoader,
} from './components';

import { OverlayReactImageCropper } from './OverlayReactImageCropper';

interface ICameraRef {
  takePhoto: () => SetStateAction<string>;
}

interface ICameraCaptureProps {
  capturedImgSrc: string;
  onDismissCamera: () => void;
  onUpdateCapturedImage: (imageURL: string) => void;
  onPreviewUpdate: (imgURL: string) => void;
  onCancelCrop: () => void;
  onImageUpload: (imgURL: string) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a camera element that loads a device camera and provides options
 * to capture the image.
 */
export function CameraCaptureWithCropper({
  capturedImgSrc,
  onDismissCamera,
  onCancelCrop,
  onImageUpload,
  onPreviewUpdate,
  onUpdateCapturedImage,
}: ICameraCaptureProps) {
  const camera = useRef<ICameraRef>(null);

  const handleSetCamera = () => {
    if (camera.current) {
      try {
        const imageURL = camera?.current.takePhoto?.();
        onUpdateCapturedImage(imageURL.toString());
      } catch (error: unknown) {
        // eslint-disable-next-line no-alert
        alert(
          'Failed to load the Camera. Looks like you have not enabled camera permissions for this app.'
        );

        // eslint-disable-next-line no-console
        console.error({ error });
      }
    }
  };

  return (
    <div
      className="
        crop-image-width flex h-[calc(100vh-10rem)] w-[calc(100vw-2rem)]
        sm:w-full
      "
    >
      {capturedImgSrc ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img alt="Captured phone " src={capturedImgSrc || ''} />
      ) : (
        <CameraLoader />
      )}

      {!capturedImgSrc && camera ? (
        <>
          <Camera
            ref={camera}
            errorMessages={{
              noCameraAccessible:
                'Sorry, could not access the camera. Please check the camera properties',
              permissionDenied:
                'Sorry, you need to permissions to access the camera.',
              switchCamera: 'Please switch the camera to continue',
              canvas: 'Sorry, could not save the image to canvas',
            }}
            facingMode="environment"
          />
          <OverlayReactImageCropper
            // previousCrop={previousCrop}
            // onPreviewMode={handleCachePreviewCrop}
            // imgSrc={capturedImgSrc}
            imgSrc={
              capturedImgSrc ||
              'https://images.unsplash.com/photo-1599202860130-f600f4948364'
            }
            onCancelCrop={onCancelCrop}
            onImageUpload={onImageUpload}
            onPreviewUpdate={onPreviewUpdate}
          />
        </>
      ) : null}

      <div
        className="
          absolute bottom-0 left-0 z-10 mt-5 flex w-full justify-between gap-1
          p-2 opacity-80 sm:mt-6
        "
      >
        <ButtonCancelCamera onClick={onDismissCamera} />
        <ButtonCaptureImage onClick={handleSetCamera} />
      </div>
    </div>
  );
}
